<template>
	<div class="file-view">
		<!--顶部栏：文件图标 + 文件名 + 计数 + 下载-->
		<div class="fv-bar">
			<div class="fv-side left">
				<i v-if="fileSuffix" class="tf-file" :class="`tf-file-${fileSuffix}`"></i>
				<span class="fv-name ellipsis">{{fileName}}</span>
				<span v-if="total > 1" class="fv-count">{{currentIndex + 1}} / {{total}}</span>
			</div>
			<!--顶栏居中的缩略图切换条（多附件时）：图片显示缩略图，其他类型显示文件图标；左右两区等分保证居中位置不随文件名长短移动-->
			<div v-if="total > 1" class="fv-thumbs">
				<div v-for="(f, i) in fileList" :key="i" class="fv-thumb" :class="{active: i == currentIndex}" @click="goIndex(i)">
					<img v-if="thumbs[i]" :src="thumbs[i]">
					<i v-else class="tf-file" :class="`tf-file-${suffixOf(f.url)}`"></i>
				</div>
			</div>
			<div class="fv-side right">
				<c-button v-if="rawUrl" prepend-icon="tf-export" @click="downloadFile">下载</c-button>
			</div>
		</div>

		<!--主预览区：按文件类型渲染-->
		<div class="fv-main">
			<!--加载中-->
			<div v-if="loading" class="fv-tip">
				<i class="tf-reload tf-spin"></i>
				<p>正在加载...</p>
			</div>
			<!--错误提示-->
			<div v-else-if="error" class="fv-tip">
				<i class="tf-cross"></i>
				<p>{{error}}</p>
			</div>
			<template v-else>
				<!--图片：内联展示（支持滚轮缩放、拖拽平移、旋转）-->
				<div v-if="kind == 'image'" class="fv-viewer" :class="{'z-drag': scale > 1, 'z-dragging': dragging}" @wheel.prevent="wheelHandle" @mousedown.prevent="dragStart" @touchstart.prevent="touchStart" @touchmove.prevent="touchMove" @touchend="touchEnd" @touchcancel="touchEnd">
					<img v-if="signedFile" :src="signedFile" :style="{transform: `translate(${moveX}px, ${moveY}px) scale(${scale}) rotate(${rotate}deg)`}" @dragstart.prevent>
				</div>
				<!--视频：内联播放-->
				<div v-else-if="kind == 'video'" class="fv-viewer">
					<video v-if="signedFile" :src="signedFile" controls autoplay></video>
				</div>
				<!--文档（含PDF）：阿里云WebOffice在线预览-->
				<div v-else-if="kind == 'doc'" ref="officeBox" class="fv-office"></div>
				<!--其他：不支持在线预览，提供下载-->
				<div v-else class="fv-other">
					<i class="tf-file" :class="`tf-file-${fileSuffix}`"></i>
					<p>{{fileName}}</p>
					<p class="tip">该格式暂不支持在线预览，请下载后查看</p>
					<c-link @click="downloadFile">下载文件</c-link>
				</div>

				<!--图片缩放/旋转工具条-->
				<div v-if="kind == 'image' && signedFile" class="fv-zoom">
					<a class="fv-zoom-btn" title="缩小" @click="zoomOut"><i class="c-icon-minus"></i></a>
					<span class="fv-zoom-percent">{{Math.round(scale * 100)}}%</span>
					<a class="fv-zoom-btn" title="放大" @click="zoomIn"><i class="c-icon-plus"></i></a>
					<i class="fv-zoom-split"></i>
					<a class="fv-zoom-text" @click="rotate = (rotate + 90) % 360"><i class="c-icon-rotate-right"></i>旋转</a>
					<a class="fv-zoom-text" @click="resetView"><i class="c-icon-refresh"></i>重置</a>
				</div>
			</template>

			<!--左右切换箭头（多附件时；独立于加载/错误态，便于随时切换）-->
			<a v-if="total > 1" class="fv-arrow left" title="上一个（←）" @click="prev"><i></i></a>
			<a v-if="total > 1" class="fv-arrow right" title="下一个（→）" @click="next"><i></i></a>
		</div>
	</div>
</template>

<script>
	//列表导出附件/图片列的中转预览页（本页需登录，未登录已被路由守卫拦截至登录页）：
	//  入口为 ?d={加密标识} —— 标识为“业务类型_业务数据ID_自定义字段ID”经AES+Base64加密，
	//  本页调 /export/fileview/list 解密换取该字段【全部】附件，支持多图/多附件切换预览，链接更短。
	//  附件列表收敛为 fileList + currentIndex，后续签名/预览/下载逻辑完全一致。
	export default {
		props: {
			//加密标识token（业务类型_数据ID_字段ID）
			d: String
		},

		data() {
			return {
				//附件列表：每项 {url, name}
				fileList: [],
				//当前查看的附件下标
				currentIndex: 0,
				//缩略图预签名地址（与fileList同序，仅图片有值）
				thumbs: [],
				//当前文件的预签名地址（图片/视频直接渲染）
				signedFile: '',
				loading: false,
				error: '',
				//图片视图状态：缩放比例、旋转角度、平移偏移、拖拽中标记
				scale: 1,
				rotate: 0,
				moveX: 0,
				moveY: 0,
				dragging: false,
				//可在线预览的文档类型（含PDF，统一走WebOffice）
				docType: [
					'doc', 'dot', 'wps', 'wpt', 'docx', 'dotx', 'docm', 'dotm', 'rtf',
					'ppt', 'pptx', 'pptm', 'ppsx', 'ppsm', 'pps', 'potx', 'potm', 'dpt', 'dps',
					'xls', 'xlt', 'et', 'xlsx', 'xltx', 'csv', 'xlsm', 'xltm',
					'pdf'
				]
			}
		},

		computed: {
			//附件总数
			total() {
				return this.fileList.length;
			},

			//当前查看的附件
			currentFile() {
				return this.fileList[this.currentIndex] || {url: '', name: ''};
			},

			//原始地址（传给签名接口的必须为未编码的原始形式）
			rawUrl() {
				return this.currentFile.url || '';
			},

			//文件信息：{name, suffix, media}
			fileInfo() {
				return this.rawUrl ? this.parseFile(this.rawUrl) : {name: '', suffix: '', media: false};
			},

			fileName() {
				let info = this.fileInfo;
				//解析不出文件名时，回退用后端返回的name
				if (!info.name) return this.currentFile.name || '未命名文件';
				return info.suffix ? `${info.name}.${info.suffix}` : info.name;
			},

			fileSuffix() {
				return (this.fileInfo.suffix || '').toLowerCase();
			},

			//预览类型：image/video/doc(含pdf)/other
			kind() {
				if (!this.rawUrl) return '';
				let info = this.fileInfo;
				if (info.media == 'image') return 'image';
				if (info.media == 'video') return 'video';
				if (this.docType.includes((info.suffix || '').toLowerCase())) return 'doc';
				return 'other';
			}
		},

		mounted() {
			//附件签名请求序号，用于丢弃过期响应
			this._signSeq = 0;
			//缩略图签名请求序号
			this._thumbSeq = 0;
			//键盘左右方向键切换（多附件时）
			this._keyHandler = e => {
				if (this.total <= 1) return;
				if (e.key == 'ArrowLeft') this.prev();
				else if (e.key == 'ArrowRight') this.next();
			};
			document.addEventListener('keydown', this._keyHandler);

			if (this.d) {
				//解密标识 → 取回该字段全部附件
				this.resolveToken();
			} else {
				//无标识时回到首页
				this.redirectTo('/');
			}
		},

		beforeDestroy() {
			this._destroyed = true;
			//移除键盘监听
			this._keyHandler && document.removeEventListener('keydown', this._keyHandler);
			//移除图片拖拽的document监听，避免页面销毁后残留
			this.dragEnd();
			//页面销毁时显式销毁WebOffice单例，避免iframe与resize监听残留
			this.clearOffice();
		},

		methods: {
			/*******解密加密标识，取回该字段全部附件*******/
			resolveToken() {
				this.loading = true;
				this.error = '';
				this.request({
					url: '/export/fileview/list',
					data: {
						d: this.d
					},
					success: data => {
						if (this._destroyed) return;
						//后端 obj = List<{url, name}>
						let files = Array.isArray(data) ? data : [];
						if (!files.length) {
							this.loading = false;
							this.error = '该字段暂无可预览的附件';
							return;
						}
						this.fileList = files.map(f => ({url: (f && f.url) || f, name: (f && f.name) || ''}));
						this.currentIndex = 0;
						this.thumbs = this.fileList.map(() => '');
						//先批量签名缩略图，再加载当前附件
						this.loadThumbs();
						this.load();
					},
					fail: (code, obj, message) => {
						if (this._destroyed) return false;
						this.loading = false;
						this.error = message || '链接无效或已失效，请重新导出后访问';
						//返回false抑制组件库的全局toast，改用页内错误提示
						return false;
					}
				});
			},

			/*******批量生成图片缩略图（urlType=3），非图片以文件图标占位*******/
			loadThumbs() {
				let paths = [];
				let idxs = [];
				this.fileList.forEach((f, i) => {
					let info = this.parseFile(f.url);
					if (info.media == 'image') {
						paths.push(f.url);
						idxs.push(i);
					}
				});
				if (!paths.length) return;
				let seq = ++this._thumbSeq;
				this.request({
					url: '/oss/weboffice/getPreViewUrl',
					data: {
						file_paths: paths,
						urlType: 3
					},
					success: data => {
						if (this._destroyed || seq != this._thumbSeq) return;
						let urls = (data && data.urls) || [];
						idxs.forEach((fi, k) => {
							if (urls[k]) this.$set(this.thumbs, fi, urls[k]);
						});
					},
					//缩略图失败不影响主预览，抑制toast
					fail: () => false
				});
			},

			/*******多附件切换（循环）*******/
			prev() {
				if (this.total <= 1) return;
				this.goIndex((this.currentIndex - 1 + this.total) % this.total);
			},

			next() {
				if (this.total <= 1) return;
				this.goIndex((this.currentIndex + 1) % this.total);
			},

			goIndex(i) {
				if (i == this.currentIndex || i < 0 || i >= this.total) return;
				this.currentIndex = i;
				this.load();
			},

			load() {
				this.clearOffice();
				this.resetView();
				this.dragEnd();
				this.signedFile = '';
				this.error = '';
				//其他类型无需签名，直接提供下载
				if (this.kind == 'other') {
					this.loading = false;
					return;
				}
				this.loading = true;
				this.signCurrent(this.rawUrl);
			},

			/*******附件鉴权（OSS私有读，访问前需获取预签名地址，urlType=0普通访问）*******/
			signCurrent(file) {
				let seq = ++this._signSeq;
				this.request({
					url: '/oss/weboffice/getPreViewUrl',
					data: {
						file_paths: [file],
						urlType: 0
					},
					loading: true,
					success: data => {
						if (this._destroyed || seq != this._signSeq) return;
						let signed = (data.urls || [])[0];
						this.loading = false;
						if (!signed) {
							this.error = '获取文件访问地址失败，请稍后重试';
							return;
						}
						if (this.kind == 'doc') {
							//文档类：WebOffice的filepath必须为签名后的URL
							this.$nextTick(() => this.initOffice(signed));
						} else {
							this.signedFile = signed;
						}
					},
					fail: () => {
						if (this._destroyed || seq != this._signSeq) return false;
						this.loading = false;
						this.error = '获取文件访问地址失败，请稍后重试';
						return false;
					}
				});
			},

			/*******WebOffice文档预览*******/
			initOffice(signed) {
				this.clearOffice();
				this._officeReq = this.request({
					url: '/oss/weboffice/token/get',
					data: {
						filepath: signed
					},
					success: data => {
						if (this._destroyed) return;
						data.time = new Date().getTime();
						this._officeToken = data;
						this.$nextTick(() => {
							let box = this.$refs.officeBox;
							//容器必须仍在文档中，避免挂载到已脱离文档的孤儿节点
							if (this._destroyed || !box || !document.contains(box)) return;
							this._office = aliyun.config({
								url: data.webofficeURL,
								mount: box
							});
							this._office.setToken({
								token: data.accessToken,
								timeout: 60 * 60 * 1000
							});
						});
						this.loopOfficeToken();
					}
				});
			},

			//WebOffice token续期
			loopOfficeToken() {
				clearTimeout(this._officeTimer);
				this._officeTimer = setTimeout(() => {
					let data = this._officeToken;
					if (!data) return;
					if (new Date().getTime() - data.time >= 50 * 60 * 1000) {
						this.request({
							url: '/oss/weboffice/token/refresh',
							data: {
								accessToken: data.accessToken,
								refreshToken: data.refreshToken
							},
							success: ndata => {
								//实例已销毁则丢弃续期结果，避免复活token与定时器
								if (this._destroyed || !this._office) return;
								ndata.time = new Date().getTime();
								this._officeToken = ndata;
								this._office.setToken({
									token: ndata.accessToken,
									timeout: 60 * 60 * 1000
								});
								this.loopOfficeToken();
							}
						});
					} else {
						this.loopOfficeToken();
					}
				}, 60000);
			},

			clearOffice() {
				clearTimeout(this._officeTimer);
				this._officeTimer = null;
				this._officeToken = null;
				this._officeReq && this._officeReq.abort && this._officeReq.abort();
				this._officeReq = null;
				//SDK为模块级单例，仅在下一次config时才销毁上一个实例，故必须显式destroy
				if (this._office && this._office.destroy) {
					try {
						this._office.destroy();
					} catch (e) {}
				}
				this._office = null;
				this.removeOfficeDom();
			},

			//兜底清理SDK残留DOM：单例iframe可能挂在已脱离文档的容器或body下
			removeOfficeDom() {
				let nodes = document.querySelectorAll('.web-office-iframe, .web-office-default-container');
				for (let i = 0; i < nodes.length; i++) {
					nodes[i].parentNode && nodes[i].parentNode.removeChild(nodes[i]);
				}
			},

			//下载：须传数据库原始地址（download内部以urlType=1签名，不可传已签名地址）
			downloadFile() {
				this.download({url: this.rawUrl});
			},

			//取地址的文件后缀（小写），用于缩略图占位图标
			suffixOf(url) {
				return (this.parseFile(url || '').suffix || '').toLowerCase();
			},

			/*******图片缩放/旋转/平移（复用扫码看图页交互）*******/
			zoomIn() {
				this.scale = Math.min(5, parseFloat((this.scale + 0.1).toFixed(2)));
			},

			zoomOut() {
				this.scale = Math.max(0.1, parseFloat((this.scale - 0.1).toFixed(2)));
			},

			//滚轮缩放：上滚放大、下滚缩小（0.1~5倍）
			wheelHandle(e) {
				//防护：ctrl+wheel（系统捏合）、拖拽中、触摸后500ms内的滚轮一律忽略——
				//触屏驱动会把单指滑动转成wheel事件，防止单指拖动误触发缩放
				if (e.ctrlKey || this.dragging || Date.now() - (this._lastTouchTs || 0) < 500) return;
				let step = e.deltaY < 0 ? 0.1 : -0.1;
				this.scale = Math.min(5, Math.max(0.1, parseFloat((this.scale + step).toFixed(2))));
			},

			//放大后拖拽平移（仅左键且scale>1时生效）
			dragStart(e) {
				//记录按下时间戳：其后500ms内的滚轮视为触屏驱动模拟，忽略
				this._lastTouchTs = Date.now();
				if (this.scale <= 1 || e.button != 0) return;
				this.dragging = true;
				this._dragStart = {x: e.clientX, y: e.clientY, mx: this.moveX, my: this.moveY};
				document.addEventListener('mousemove', this.dragMove);
				document.addEventListener('mouseup', this.dragEnd);
			},

			dragMove(e) {
				if (!this.dragging) return;
				this.moveX = this._dragStart.mx + e.clientX - this._dragStart.x;
				this.moveY = this._dragStart.my + e.clientY - this._dragStart.y;
			},

			dragEnd() {
				if (!this.dragging) return;
				this.dragging = false;
				document.removeEventListener('mousemove', this.dragMove);
				document.removeEventListener('mouseup', this.dragEnd);
			},

			//触屏单指拖动平移
			touchStart(e) {
				this._lastTouchTs = Date.now();
				if (e.touches.length >= 1) {
					this._touchStart = {x: e.touches[0].clientX, y: e.touches[0].clientY, mx: this.moveX, my: this.moveY};
					this.dragging = true;
				}
			},

			touchMove(e) {
				this._lastTouchTs = Date.now();
				if (this._touchStart && e.touches.length >= 1) {
					this.moveX = this._touchStart.mx + e.touches[0].clientX - this._touchStart.x;
					this.moveY = this._touchStart.my + e.touches[0].clientY - this._touchStart.y;
				}
			},

			touchEnd(e) {
				this._lastTouchTs = Date.now();
				if (e.touches.length == 0) {
					this._touchStart = null;
					this.dragging = false;
				}
			},

			//重置缩放、旋转与平移
			resetView() {
				this.scale = 1;
				this.rotate = 0;
				this.moveX = 0;
				this.moveY = 0;
			}
		}
	}
</script>

<style lang="stylus" scoped>
	.file-view {
		display: flex;
		flex-direction: column;
		width: 100vw;
		height: 100vh;
		background: #F4F6F9;
		overflow: hidden;
	}

	.fv-bar {
		display: flex;
		align-items: center;
		flex-shrink: 0;
		gap: 10px;
		height: 52px;
		padding: 0 16px;
		background: #fff;
		border-bottom: 1px solid #E5E6EB;

		.tf-file {
			font-size: 20px;
			color: #86909C;
		}

		.fv-name {
			max-width: 50vw;
			font-size: 14px;
			font-weight: 500;
			color: #1D2129;
		}

		.fv-count {
			flex-shrink: 0;
			padding: 2px 8px;
			border-radius: 10px;
			background: #F2F3F5;
			font-size: 12px;
			color: #4E5969;
		}

		//左右两区等分剩余空间（flex-basis 0），使中间缩略图条始终绝对居中，不随文件名长短移动
		.fv-side {
			display: flex;
			align-items: center;
			gap: 10px;
			flex: 1 1 0;
			min-width: 0;

			&.right {
				justify-content: flex-end;
			}
		}
	}

	.fv-main {
		position: relative;
		flex-grow: 1;
		min-height: 0;
		overflow: hidden;
	}

	.fv-viewer {
		display: flex;
		align-items: center;
		justify-content: center;
		height: 100%;
		overflow: hidden;
		//禁用浏览器默认触屏手势（双指缩放页面/滚动），交由本页手势处理
		touch-action: none;

		img, video {
			max-width: 92%;
			max-height: 92%;
		}

		img {
			transition: transform .1s;
		}

		//放大后可拖拽平移的光标提示
		&.z-drag {
			cursor: move;
		}

		&.z-dragging {
			cursor: grabbing;

			img {
				transition: none;
			}
		}
	}

	//左右切换箭头（垂直居中悬浮，CSS绘制chevron，不依赖图标字体）
	.fv-arrow {
		position: absolute;
		top: 50%;
		z-index: 12;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 44px;
		height: 44px;
		margin-top: -22px;
		border-radius: 50%;
		background: rgba(0, 0, 0, .35);
		cursor: pointer;
		transition: background .2s, transform .1s;

		&:hover {
			background: #05AD5D;
		}

		&:active {
			transform: scale(.92);
		}

		&.left {
			left: 16px;
		}

		&.right {
			right: 16px;
		}

		i {
			width: 12px;
			height: 12px;
			border-top: 2px solid #fff;
			border-right: 2px solid #fff;
		}

		&.left i {
			margin-left: 4px;
			transform: rotate(-135deg);
		}

		&.right i {
			margin-right: 4px;
			transform: rotate(45deg);
		}
	}

	//图片缩放/旋转工具条（底部居中悬浮）
	.fv-zoom {
		position: absolute;
		bottom: 16px;
		left: 50%;
		z-index: 10;
		display: flex;
		align-items: center;
		gap: 12px;
		height: 44px;
		padding: 0 12px;
		border-radius: 8px;
		background: rgba(0, 0, 0, .45);
		backdrop-filter: blur(8px);
		color: #fff;
		font-size: 13px;
		transform: translateX(-50%);

		&-btn {
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
			width: 32px;
			height: 32px;
			border-radius: 50%;
			font-size: 18px;
			color: #fff;
			background: rgba(255, 255, 255, .22);
			cursor: pointer;
			transition: background .2s, transform .1s;

			&:hover {
				background: #05AD5D;
			}

			&:active {
				transform: scale(.92);
			}
		}

		&-percent {
			min-width: 44px;
			text-align: center;
		}

		&-split {
			width: 1px;
			height: 18px;
			background: rgba(255, 255, 255, .3);
		}

		&-text {
			display: flex;
			align-items: center;
			gap: 4px;
			padding: 6px 8px;
			cursor: pointer;

			i {
				font-size: 15px;
			}
		}
	}

	//顶栏居中的缩略图切换条（内嵌于顶部栏，尺寸适配栏高）
	.fv-thumbs {
		display: flex;
		align-items: center;
		//不拉伸不收缩：宽度由内容决定（上限 max-width），配合左右两区等分锁定居中位置
		flex: 0 0 auto;
		gap: 6px;
		max-width: 40vw;
		overflow-x: auto;
		//隐藏横向滚动条，保持顶栏整洁
		scrollbar-width: none;
		&::-webkit-scrollbar {
			display: none;
		}

		.fv-thumb {
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
			width: 32px;
			height: 32px;
			border: 2px solid transparent;
			border-radius: 4px;
			background: #F2F3F5;
			overflow: hidden;
			cursor: pointer;
			transition: border-color .2s;

			img {
				width: 100%;
				height: 100%;
				object-fit: cover;
			}

			i {
				font-size: 16px;
				color: #86909C;
			}

			&.active {
				border-color: #00B38A;
			}
		}
	}

	.fv-office {
		width: 100%;
		height: 100%;
	}

	.fv-tip, .fv-other {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 12px;
		height: 100%;

		i {
			font-size: 56px;
			color: #C9CDD4;
		}

		p {
			margin: 0;
			font-size: 14px;
			color: #4E5969;
		}

		.tip {
			font-size: 12px;
			color: #86909C;
		}
	}

	//单行省略（顶部文件名）
	.ellipsis {
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}
</style>
